import { DOCUMENT } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";

import { appPath } from "@azm/core/utilities/app-base-path";

import { ICONS_SPRITE_PATH } from "./icon.constants";

export const ICON_SPRITE_HOST_ID = "azm-general-icons-sprite";

@Injectable({
	providedIn: "root",
})
export class IconSpriteService {
	readonly #document = inject(DOCUMENT);
	readonly #ready = signal(false);
	#prefetchStarted = false;

	readonly ready = this.#ready.asReadonly();

	prefetch(): void {
		if (this.#prefetchStarted || this.#ready()) {
			return;
		}

		const hostDoc = this.#document;
		if (hostDoc.getElementById(ICON_SPRITE_HOST_ID)) {
			this.#ready.set(true);
			return;
		}

		const view = hostDoc.defaultView;
		if (!view) {
			return;
		}

		this.#prefetchStarted = true;
		const url = appPath(ICONS_SPRITE_PATH);

		view
			.fetch(url, { cache: "force-cache", credentials: "same-origin" })
			.then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
			.then((markup) => this.#injectSprite(markup))
			.catch(() => {
				this.#prefetchStarted = false;
			});
	}

	#injectSprite(markup: string): void {
		const hostDoc = this.#document;
		if (hostDoc.getElementById(ICON_SPRITE_HOST_ID)) {
			this.#ready.set(true);
			return;
		}

		const parsed = new hostDoc.defaultView!.DOMParser().parseFromString(markup, "image/svg+xml");
		if (parsed.querySelector("parsererror")) {
			this.#prefetchStarted = false;
			return;
		}

		const host = hostDoc.createElementNS("http://www.w3.org/2000/svg", "svg");
		host.id = ICON_SPRITE_HOST_ID;
		host.setAttribute("aria-hidden", "true");
		host.setAttribute("focusable", "false");
		host.setAttribute("style", "position:absolute;width:0;height:0;overflow:hidden");

		parsed.querySelectorAll("symbol").forEach((symbol) => {
			host.appendChild(hostDoc.importNode(symbol, true));
		});

		hostDoc.body.prepend(host);
		this.#ready.set(true);
	}
}
